import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import { calculateAfterBid } from "../../helper/common/modifyValueByPercentage";
import {
  ADJUSTMENT_TYPE_LABELS,
  mapUiActionToAdjustmentType,
} from "../../helper/bulkSelection/bulkPlacementBidUpdate";

const BASE_ADJUSTMENT_OPTIONS = [
  {
    key: "increase-by",
    label: "Increase by %",
    hint: "Raise bids by a percentage",
    unit: "%",
    defaultValue: 5,
    Icon: TrendingUpRoundedIcon,
    accent: "#12B76A",
    accentBg: "#ECFDF3",
  },
  {
    key: "decrease-by",
    label: "Decrease by %",
    hint: "Lower bids by a percentage",
    unit: "%",
    defaultValue: 5,
    Icon: TrendingDownRoundedIcon,
    accent: "#F04438",
    accentBg: "#FEF3F2",
  },
  {
    key: "increase-by-abs",
    label: "Increase absolute",
    hint: "Add a fixed amount to bids",
    unit: "",
    defaultValue: 5,
    Icon: AddRoundedIcon,
    accent: "#12B76A",
    accentBg: "#ECFDF3",
  },
  {
    key: "decrease-by-abs",
    label: "Decrease absolute",
    hint: "Subtract a fixed amount from bids",
    unit: "",
    defaultValue: 5,
    Icon: RemoveRoundedIcon,
    accent: "#F04438",
    accentBg: "#FEF3F2",
  },
  {
    key: "set_bid_as",
    label: "Set absolute value",
    hint: "Overwrite bid with a fixed value",
    unit: "",
    defaultValue: "",
    Icon: TuneRoundedIcon,
    accent: "#6B4EFF",
    accentBg: "#F3EFFF",
  },
];

const COPY_CPC_OPTION = {
  key: "copy_cpc",
  label: "Copy CPC",
  hint: "Set bid equal to the row CPC",
  unit: "",
  defaultValue: "",
  Icon: ContentCopyRoundedIcon,
  accent: "#2E90FA",
  accentBg: "#EFF8FF",
  noValue: true,
};

function formatBidValue(value, { asInteger = false, fractionDigits } = {}) {
  if (value === null || value === undefined || value === "" || Number.isNaN(Number(value))) {
    return "N/A";
  }
  const numeric = Number(value);
  if (typeof fractionDigits === "number") {
    if (fractionDigits === 0) return Math.round(numeric);
    return Number(numeric.toFixed(fractionDigits));
  }
  if (asInteger) {
    return Math.round(numeric);
  }
  return Number(parseFloat(numeric).toFixed(2));
}

function formatBidUnit(bidRules) {
  const unit = bidRules?.unit || bidRules?.currencyCode || "";
  if (unit === "%") return "%";
  return unit ? ` ${unit}` : "";
}

function formatMinBidLabel(bidRules) {
  if (!bidRules) return "";
  return `${bidRules.minBid}${formatBidUnit(bidRules)}`;
}

const MISSING_BID_FALLBACK = {
  SKIP: "skip",
  DEFAULT: "default",
};

function isMissingBidValue(value) {
  return (
    value === null ||
    value === undefined ||
    value === "" ||
    Number.isNaN(Number(value))
  );
}

/**
 * Attractive MUI modal for V2 bulk bid adjust.
 * Placement: 5 options. Targeting: pass showCopyCpc to include Copy CPC.
 */
export default function BulkAdjustBidModalV2({
  open = false,
  title = "Adjust Collective Bid",
  subtitle = "Choose how you want to update bids for the selected rows.",
  previewRows = [],
  isPercentageFieldsDisabled = false,
  detailedText = null,
  showCopyCpc = false,
  cpcKey = "cpc",
  previewLabelKey = "placement_name",
  previewSecondaryKey = "campaign_name",
  previewValueKey = null,
  previewAsInteger = false,
  /** Bid rules: { unit|currencyCode, minBid, fractionDigits, roundBid }. */
  bidRules = null,
  /** Noun used in min/validation copy — "bid" (default) or "budget". */
  valueNoun = "bid",
  infoNote = null,
  /** Placement Bids: Skip vs fallback base bid when current bid is missing. */
  enableMissingBidFallback = false,
  onClose,
  onSubmit,
}) {
  const adjustmentOptions = useMemo(
    () => (showCopyCpc ? [...BASE_ADJUSTMENT_OPTIONS, COPY_CPC_OPTION] : BASE_ADJUSTMENT_OPTIONS),
    [showCopyCpc]
  );

  const [selectedAction, setSelectedAction] = useState("");
  const [valueByAction, setValueByAction] = useState({
    "increase-by": 5,
    "decrease-by": 5,
    "increase-by-abs": 5,
    "decrease-by-abs": 5,
    set_bid_as: "",
    copy_cpc: "",
  });
  const [missingBidFallbackMode, setMissingBidFallbackMode] = useState("");
  const [defaultBaseValue, setDefaultBaseValue] = useState("");

  useEffect(() => {
    if (!open) return;
    setSelectedAction("");
    setValueByAction({
      "increase-by": 5,
      "decrease-by": 5,
      "increase-by-abs": 5,
      "decrease-by-abs": 5,
      set_bid_as: "",
      copy_cpc: "",
    });
    setMissingBidFallbackMode("");
    setDefaultBaseValue("");
  }, [open]);

  const selectedOption = adjustmentOptions.find((o) => o.key === selectedAction);
  const currentValue = selectedAction ? valueByAction[selectedAction] : "";
  const isCopyCpc = selectedAction === "copy_cpc";

  const isPercentageAction =
    selectedAction === "increase-by" || selectedAction === "decrease-by";
  const showMissingBidFallback = enableMissingBidFallback && isPercentageAction;

  const bidPreviewData = useMemo(() => {
    if (!selectedAction || !previewRows?.length) return [];
    return previewRows.map((row, index) => {
      const currentBid = previewValueKey
        ? row?.[previewValueKey]
        : row?.bid ??
          row?.bid_adjustment_value ??
          row?.current_bid ??
          row?.campaign_budget;
      const cpcValue = row?.[cpcKey];
      const isMissingBid = isMissingBidValue(currentBid);
      let afterBid = null;
      let afterBidLabel = null;
      let skipBidValidation = false;

      if (showMissingBidFallback && isMissingBid) {
        if (missingBidFallbackMode === MISSING_BID_FALLBACK.SKIP) {
          afterBidLabel = "Skip";
          skipBidValidation = true;
        } else if (missingBidFallbackMode !== MISSING_BID_FALLBACK.DEFAULT) {
          skipBidValidation = true;
        } else if (
          defaultBaseValue === "" ||
          Number.isNaN(Number(defaultBaseValue))
        ) {
          skipBidValidation = true;
        } else {
          afterBid = bidRules?.roundBid
            ? bidRules.roundBid(Number(defaultBaseValue))
            : Number(defaultBaseValue);
        }
      } else {
        afterBid = calculateAfterBid({
          selectedAction,
          valuePercentage: currentValue,
          setAsValue: currentValue,
          currentBid,
          cpcValue,
          minBid: bidRules?.minBid ?? 0,
        });
        if (afterBid != null && bidRules?.roundBid) {
          afterBid = bidRules.roundBid(afterBid);
          // Keep decrease % / absolute results at/above min after rounding.
          if (
            selectedAction === "decrease-by" ||
            selectedAction === "decrease-by-abs"
          ) {
            afterBid = Math.max(afterBid, bidRules.minBid);
          }
        }
      }
      return {
        id: `${row?.target_id || row?.campaign_id || "row"}-${row?.[previewLabelKey] || index}`,
        label: row?.[previewLabelKey] || row?.targeting || row?.campaign_name || "-",
        campaignName: row?.[previewSecondaryKey] || row?.campaign_name || "-",
        currentBid,
        afterBid,
        afterBidLabel,
        skipBidValidation,
      };
    });
  }, [
    selectedAction,
    previewRows,
    currentValue,
    cpcKey,
    previewLabelKey,
    previewSecondaryKey,
    previewValueKey,
    bidRules,
    showMissingBidFallback,
    missingBidFallbackMode,
    defaultBaseValue,
  ]);

  const bidValidationError = useMemo(() => {
    if (!bidRules || !selectedAction) return null;
    const { minBid, roundBid } = bidRules;
    const minLabel = formatMinBidLabel(bidRules);

    const noun = valueNoun || "bid";

    if (selectedAction === "set_bid_as") {
      if (currentValue === "") return null;
      const rounded = roundBid?.(currentValue);
      if (rounded === null || Number.isNaN(Number(rounded))) {
        return `Enter a valid ${noun} (min ${minLabel}).`;
      }
      if (rounded < minBid) {
        return `${noun.charAt(0).toUpperCase()}${noun.slice(1)} must be at least ${minLabel}.`;
      }
    }

    if (!bidPreviewData.length) return null;

    const rowsToValidate = bidPreviewData.filter((row) => !row.skipBidValidation);
    if (!rowsToValidate.length) return null;

    const hasInvalidAfter = rowsToValidate.some(
      (row) =>
        row.afterBid === null ||
        row.afterBid === undefined ||
        row.afterBid === "" ||
        Number.isNaN(Number(row.afterBid))
    );
    if (hasInvalidAfter) {
      if (selectedAction === "copy_cpc") {
        return `Some rows have an invalid or missing CPC. CPC must be at least ${minLabel}.`;
      }
      return `Some rows have an invalid resulting ${noun}. Adjust the value and try again.`;
    }

    const hasBelowMin = rowsToValidate.some(
      (row) => Number(row.afterBid) < minBid
    );
    if (!hasBelowMin) return null;

    if (selectedAction === "copy_cpc") {
      return `Some CPCs are below the minimum bid of ${minLabel}. Adjust selection and try again.`;
    }

    return `Some ${noun}s are below the minimum of ${minLabel}. Adjust the value and try again.`;
  }, [bidRules, selectedAction, currentValue, bidPreviewData, valueNoun]);

  const previewFormatOpts = useMemo(() => {
    if (bidRules) {
      return { fractionDigits: bidRules.fractionDigits };
    }
    return { asInteger: previewAsInteger };
  }, [bidRules, previewAsInteger]);

  const valueInputStep = useMemo(() => {
    if (!bidRules) return "any";
    if (isPercentageAction) return "any";
    return bidRules.fractionDigits === 0 ? "1" : "0.01";
  }, [bidRules, isPercentageAction]);

  const isSubmitDisabled = useMemo(() => {
    if (!selectedAction) return true;
    if (isPercentageAction && isPercentageFieldsDisabled) return true;
    if (bidValidationError) return true;
    if (isCopyCpc) return false;
    const parsed = Number(currentValue);
    if (currentValue === "" || Number.isNaN(parsed)) return true;
    if (selectedAction === "set_bid_as" && parsed < 0) return true;
    if (selectedAction !== "set_bid_as" && parsed <= 0) return true;
    if (showMissingBidFallback) {
      if (
        missingBidFallbackMode !== MISSING_BID_FALLBACK.SKIP &&
        missingBidFallbackMode !== MISSING_BID_FALLBACK.DEFAULT
      ) {
        return true;
      }
      if (missingBidFallbackMode === MISSING_BID_FALLBACK.DEFAULT) {
        const parsedDefault = Number(defaultBaseValue);
        if (
          defaultBaseValue === "" ||
          Number.isNaN(parsedDefault) ||
          parsedDefault < 0
        ) {
          return true;
        }
      }
    }
    return false;
  }, [
    selectedAction,
    currentValue,
    isPercentageAction,
    isPercentageFieldsDisabled,
    isCopyCpc,
    bidValidationError,
    showMissingBidFallback,
    missingBidFallbackMode,
    defaultBaseValue,
  ]);

  const handleValueChange = (raw) => {
    const trimmed = String(raw ?? "").trim();
    if (trimmed === "" || !Number.isNaN(Number(trimmed))) {
      setValueByAction((prev) => ({ ...prev, [selectedAction]: trimmed }));
    }
  };

  const handleDefaultBaseValueChange = (raw) => {
    const trimmed = String(raw ?? "").trim();
    if (trimmed === "" || !Number.isNaN(Number(trimmed))) {
      setDefaultBaseValue(trimmed);
    }
  };

  const handleSubmit = () => {
    if (isSubmitDisabled) return;
    const adjustmentType = mapUiActionToAdjustmentType(selectedAction);
    let value = isCopyCpc
      ? null
      : selectedAction === "set_bid_as"
        ? Number(currentValue)
        : Number(currentValue);

    if (
      value != null &&
      bidRules?.roundBid &&
      (selectedAction === "set_bid_as" ||
        selectedAction === "increase-by-abs" ||
        selectedAction === "decrease-by-abs")
    ) {
      value = bidRules.roundBid(value);
    }

    let resolvedDefaultBaseValue;
    if (
      showMissingBidFallback &&
      missingBidFallbackMode === MISSING_BID_FALLBACK.DEFAULT
    ) {
      resolvedDefaultBaseValue = Number(defaultBaseValue);
      if (bidRules?.roundBid) {
        resolvedDefaultBaseValue = bidRules.roundBid(resolvedDefaultBaseValue);
      }
    }

    onSubmit?.({
      selectedAction,
      valuePercentage: selectedAction === "set_bid_as" || isCopyCpc ? null : value,
      setAsValue: selectedAction === "set_bid_as" ? value : null,
      adjustmentType,
      value,
      label: ADJUSTMENT_TYPE_LABELS[adjustmentType] || selectedOption?.label,
      defaultBaseValue: resolvedDefaultBaseValue,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 16px 48px rgba(16, 24, 40, 0.18)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 2.5,
          pb: 1.5,
          mb: 1.5,
          background: "linear-gradient(135deg, #F7F5FF 0%, #FFFFFF 55%)",
          borderBottom: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#111" }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}>
              {subtitle}
            </Typography>
            {detailedText ? (
              <Typography sx={{ fontSize: "11px", color: "error.main", mt: 0.75 }}>
                * {detailedText}
              </Typography>
            ) : null}
            {infoNote ? (
              <Typography sx={{ fontSize: "11px", color: "error.main", mt: 0.75 }}>
                * {infoNote}
              </Typography>
            ) : null}
            {bidRules ? (
              <Typography sx={{ fontSize: "11px", color: "text.secondary", mt: 0.75 }}>
                Min {valueNoun || "bid"}: {formatMinBidLabel(bidRules)}
                {bidRules.fractionDigits === 0 ? " (whole numbers)" : " (2 decimals)"}
              </Typography>
            ) : null}
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 1, pb: 2.5 }}>
        <Stack spacing={2}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
              },
              gap: 1.25,
            }}
          >
            {adjustmentOptions.map((option) => {
              const selected = selectedAction === option.key;
              const disabled =
                isPercentageFieldsDisabled &&
                (option.key === "increase-by" || option.key === "decrease-by");
              const Icon = option.Icon;
              return (
                <Box
                  key={option.key}
                  onClick={() => {
                    if (disabled) return;
                    setSelectedAction(option.key);
                  }}
                  sx={{
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.5 : 1,
                    border: "1px solid",
                    borderColor: selected ? option.accent : "grey.200",
                    borderRadius: 2,
                    px: 1.5,
                    py: 1.25,
                    bgcolor: selected ? option.accentBg : "#fff",
                    transition: "all 0.15s ease",
                    "&:hover": disabled
                      ? undefined
                      : {
                          borderColor: option.accent,
                          bgcolor: option.accentBg,
                        },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Radio
                      checked={selected}
                      disabled={disabled}
                      size="small"
                      sx={{
                        p: 0.25,
                        color: option.accent,
                        "&.Mui-checked": { color: option.accent },
                      }}
                    />
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "8px",
                        bgcolor: option.accentBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon sx={{ fontSize: 16, color: option.accent }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: "13px", fontWeight: 650, color: "#111" }}>
                        {option.label}
                      </Typography>
                      <Typography sx={{ fontSize: "11px", color: "text.secondary", mt: 0.25 }}>
                        {option.hint}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Box>

          {selectedAction && !isCopyCpc ? (
            <Box
              sx={{
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: 2,
                bgcolor: "#FAFAFB",
                px: 2,
                py: 1.75,
              }}
            >
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#333", mb: 1 }}>
                {selectedOption?.label} value
              </Typography>
              <TextField
                size="small"
                type="number"
                value={currentValue}
                placeholder="Enter value"
                onChange={(e) => handleValueChange(e.target.value)}
                inputProps={{ min: 0, step: valueInputStep }}
                InputProps={{
                  endAdornment: selectedOption?.unit ? (
                    <InputAdornment position="end">
                      <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
                        {selectedOption.unit}
                      </Typography>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  maxWidth: 220,
                  "& .MuiInputBase-root": {
                    height: 36,
                    fontSize: "13px",
                    bgcolor: "#fff",
                  },
                }}
              />
              {bidValidationError ? (
                <Typography sx={{ fontSize: "11px", color: "error.main", mt: 1 }}>
                  {bidValidationError}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {showMissingBidFallback ? (
            <Box
              sx={{
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: 2,
                bgcolor: "#FAFAFB",
                px: 2,
                py: 1.75,
              }}
            >
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#333" }}>
                If a placement has no bid
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "text.secondary", mt: 0.5, mb: 1.25 }}>
                Percentage changes need a current bid. Choose Skip to leave those
                placements unchanged, or set a fallback bid. The fallback is saved
                as-is — the percentage is not applied to it.
              </Typography>
              <Stack spacing={1}>
                {[
                  {
                    key: MISSING_BID_FALLBACK.SKIP,
                    label: "Skip",
                    hint: "Leave placements with no bid unchanged. They will not be updated.",
                  },
                  {
                    key: MISSING_BID_FALLBACK.DEFAULT,
                    label: "Use a fallback bid",
                    hint: "Set the missing bid to this value. The percentage is not applied.",
                  },
                ].map((option) => {
                  const selected = missingBidFallbackMode === option.key;
                  return (
                    <Box
                      key={option.key}
                      onClick={() => setMissingBidFallbackMode(option.key)}
                      sx={{
                        cursor: "pointer",
                        border: "1px solid",
                        borderColor: selected ? "#6B4EFF" : "grey.200",
                        borderRadius: 1.5,
                        px: 1.25,
                        py: 1,
                        bgcolor: selected ? "#F3EFFF" : "#fff",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Radio
                          checked={selected}
                          onChange={() => setMissingBidFallbackMode(option.key)}
                          size="small"
                          sx={{
                            p: 0.25,
                            color: "#6B4EFF",
                            "&.Mui-checked": { color: "#6B4EFF" },
                          }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: "13px", fontWeight: 650, color: "#111" }}>
                            {option.label}
                          </Typography>
                          <Typography sx={{ fontSize: "11px", color: "text.secondary", mt: 0.25 }}>
                            {option.hint}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
              {missingBidFallbackMode === MISSING_BID_FALLBACK.DEFAULT ? (
                <Box sx={{ mt: 1.5 }}>
                  <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#333", mb: 1 }}>
                    Fallback bid
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={defaultBaseValue}
                    placeholder="Enter fallback bid"
                    onChange={(e) => handleDefaultBaseValueChange(e.target.value)}
                    inputProps={{ min: 0, step: bidRules?.fractionDigits === 0 ? "1" : "any" }}
                    InputProps={{
                      endAdornment: bidRules?.unit ? (
                        <InputAdornment position="end">
                          <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
                            {bidRules.unit}
                          </Typography>
                        </InputAdornment>
                      ) : null,
                    }}
                    sx={{
                      maxWidth: 220,
                      "& .MuiInputBase-root": {
                        height: 36,
                        fontSize: "13px",
                        bgcolor: "#fff",
                      },
                    }}
                  />
                  <Typography sx={{ fontSize: "11px", color: "text.secondary", mt: 0.75 }}>
                    Used only when a placement has no bid. This value is set as-is.
                    Enter 0 or higher.
                  </Typography>
                </Box>
              ) : null}
            </Box>
          ) : null}

          {isCopyCpc ? (
            <Box
              sx={{
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: 2,
                bgcolor: "#EFF8FF",
                px: 2,
                py: 1.5,
              }}
            >
              <Typography sx={{ fontSize: "12px", color: "#333" }}>
                Bid will be set to each targeting&apos;s CPC.
              </Typography>
              {bidValidationError ? (
                <Typography sx={{ fontSize: "11px", color: "error.main", mt: 1 }}>
                  {bidValidationError}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {bidPreviewData.length > 0 && selectedAction ? (
            <Box>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#333", mb: 1 }}>
                Preview ({Math.min(bidPreviewData.length, 8)}
                {bidPreviewData.length > 8 ? ` of ${bidPreviewData.length}` : ""})
              </Typography>
              <TableContainer
                sx={{
                  maxHeight: 220,
                  border: "1px solid",
                  borderColor: "grey.200",
                  borderRadius: 2,
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: "11px", fontWeight: 700 }}>Row</TableCell>
                      <TableCell sx={{ fontSize: "11px", fontWeight: 700 }} align="right">
                        Current
                      </TableCell>
                      <TableCell sx={{ fontSize: "11px", fontWeight: 700 }} align="right">
                        After
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bidPreviewData.slice(0, 8).map((row) => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ fontSize: "12px" }}>
                          <Typography sx={{ fontSize: "12px", color: "#333" }} noWrap>
                            {row.label}
                          </Typography>
                          <Typography sx={{ fontSize: "10px", color: "text.secondary" }} noWrap>
                            {row.campaignName}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: "12px" }} align="right">
                          {formatBidValue(row.currentBid, previewFormatOpts)}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "12px",
                            fontWeight: 650,
                            color: row.afterBidLabel ? "text.secondary" : "#6B4EFF",
                          }}
                          align="right"
                        >
                          {row.afterBidLabel ||
                            formatBidValue(row.afterBid, previewFormatOpts)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 600,
            borderColor: "grey.400",
            color: "#333",
            minWidth: 96,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitDisabled}
          sx={{
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 600,
            minWidth: 140,
            boxShadow: "none",
            bgcolor: "#6B4EFF",
            "&:hover": { bgcolor: "#5A3FE0", boxShadow: "none" },
          }}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}
